"use client"

import * as React from "react"
import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react"
import type { ParentBridge } from "@humblytics/editor-core"

// ============================================================================
// Types
// ============================================================================

export type SidebarTab = 'chat' | 'editor';

/**
 * Which surface the chat is currently embedded in. Drives suggested prompts,
 * slash-command filtering, and the `context` stamp on outgoing messages so a
 * single chat history can separate onboarding / dashboard / editor turns.
 */
type ChatSurface = 'onboarding' | 'dashboard' | 'split_test_editor';

export interface EditorElementData {
  selector: string;
  tagName: string;
  textContent: string;
  computedStyles: Record<string, string>;
  _revision?: number;
  initialTab?: 'text' | 'styles' | 'image';
  src?: string;
  alt?: string;
}

interface AISidebarContextValue {
  /** Whether the AI sidebar is open */
  isOpen: boolean
  /** Open the sidebar */
  open: () => void
  /** Close the sidebar */
  close: () => void
  /** Toggle the sidebar */
  toggle: () => void
  /** Set sidebar state */
  setIsOpen: (open: boolean) => void
  /** Open sidebar with a pre-filled query */
  openWithQuery: (query: string) => void
  /** The pre-filled query (if any) */
  pendingQuery: string | null
  /** Clear the pending query */
  clearPendingQuery: () => void
  /** Active sidebar tab */
  activeTab: SidebarTab
  /** Set the active tab */
  setActiveTab: (tab: SidebarTab) => void
  /** Currently selected editor element */
  editorElement: EditorElementData | null
  /** Set the editor element */
  setEditorElement: (data: EditorElementData | null) => void
  /** Whether the editor shell is mounted */
  isEditorMode: boolean
  /** Set editor mode */
  setIsEditorMode: (value: boolean) => void
  /** Bridge ref for iframe communication */
  editorBridge: ParentBridge | null
  /** Set the editor bridge */
  setEditorBridge: (bridge: ParentBridge | null) => void
  /** Convenience: set element + switch tab + open sidebar */
  openEditorTab: (element: EditorElementData) => void
  /** Callback to open editor in a new browser tab (fallback for iframe issues) */
  onOpenInNewTab: (() => void) | null
  /** Set the open-in-new-tab callback */
  setOnOpenInNewTab: (cb: (() => void) | null) => void
  /** Which surface the chat is rendering on. */
  surface: ChatSurface
  /** Switch the active chat surface (e.g. editor mount/unmount). */
  setSurface: (surface: ChatSurface) => void
  /** Optional pinned specialist for the next turn (slash command equivalent). */
  forceSpecialist: string | null
  /** Pin / clear the next-turn specialist. */
  setForceSpecialist: (role: string | null) => void
  /**
   * Regenerate the assistant message with the given id. Set by `AISidebar`
   * via `registerRegenerate(useAIChat.regenerate)` so other surfaces can
   * call it without depending on the hook directly.
   */
  regenerate: ((messageId: string) => void) | null
  registerRegenerate: (fn: ((messageId: string) => void) | null) => void
}

// ============================================================================
// Context
// ============================================================================

export const AISidebarContext = createContext<AISidebarContextValue | undefined>(undefined)

// ============================================================================
// Provider
// ============================================================================

interface AISidebarProviderProps {
  children: ReactNode
  /** Initial open state (default: false) */
  defaultOpen?: boolean
  /**
   * Kept for backwards compatibility with callers that always want the
   * panel open (e.g. nocode editor). Panel open state is no longer
   * persisted across reloads, so this no longer needs to "skip" anything —
   * it's equivalent to `defaultOpen`.
   */
  forceOpen?: boolean
}

export function AISidebarProvider({ children, defaultOpen = false }: AISidebarProviderProps) {
  // Panel always starts closed (or `defaultOpen`) on load — it is never
  // restored from a previous session so it can't reopen over page content.
  const [isOpen, setIsOpenState] = useState<boolean>(defaultOpen)
  
  const [pendingQuery, setPendingQuery] = useState<string | null>(null)

  // Editor tab state (NOT persisted)
  const [activeTab, setActiveTabState] = useState<SidebarTab>('chat')
  const [editorElement, setEditorElementState] = useState<EditorElementData | null>(null)
  const [isEditorMode, setIsEditorModeState] = useState(false)
  const [editorBridge, setEditorBridgeState] = useState<ParentBridge | null>(null)
  const [onOpenInNewTab, setOnOpenInNewTabState] = useState<(() => void) | null>(null)

  // Surface + specialist routing + regenerate registration
  const [surface, setSurfaceState] = useState<ChatSurface>('dashboard')
  const [forceSpecialist, setForceSpecialistState] = useState<string | null>(null)
  const regenerateRef = useRef<((id: string) => void) | null>(null)
  // Wrapping function so context consumers get a stable reference.
  const regenerate = useCallback((id: string) => {
    regenerateRef.current?.(id)
  }, [])

  // Auto-reset when leaving editor mode
  useEffect(() => {
    if (!isEditorMode) {
      setActiveTabState('chat')
      setEditorElementState(null)
      setEditorBridgeState(null)
      setOnOpenInNewTabState(null)
    }
  }, [isEditorMode])

  const open = useCallback(() => setIsOpenState(true), [])
  const close = useCallback(() => setIsOpenState(false), [])
  const toggle = useCallback(() => setIsOpenState(prev => !prev), [isOpen])
  const setIsOpen = useCallback((open: boolean) => setIsOpenState(open), [])
  
  const openWithQuery = useCallback((query: string) => {
    setPendingQuery(query)
    setIsOpenState(true)
  }, [])
  
  const clearPendingQuery = useCallback(() => {
    setPendingQuery(null)
  }, [])

  const setActiveTab = useCallback((tab: SidebarTab) => {
    setActiveTabState(tab)
  }, [])

  const setEditorElement = useCallback((data: EditorElementData | null) => {
    setEditorElementState(data)
  }, [])

  const setIsEditorMode = useCallback((value: boolean) => {
    setIsEditorModeState(value)
  }, [])

  const setEditorBridge = useCallback((bridge: ParentBridge | null) => {
    setEditorBridgeState(bridge)
  }, [])

  const setOnOpenInNewTab = useCallback((cb: (() => void) | null) => {
    // Wrap in function to avoid React treating the callback as a state updater
    setOnOpenInNewTabState(() => cb)
  }, [])

  const setSurface = useCallback((next: ChatSurface) => {
    setSurfaceState(next)
  }, [])

  const setForceSpecialist = useCallback((role: string | null) => {
    setForceSpecialistState(role)
  }, [])

  const registerRegenerate = useCallback((fn: ((id: string) => void) | null) => {
    regenerateRef.current = fn
  }, [])

  const selectionRevisionRef = useRef(0)

  const openEditorTab = useCallback((element: EditorElementData) => {
    selectionRevisionRef.current += 1
    setEditorElementState({
      ...element,
      _revision: selectionRevisionRef.current,
    })
    setActiveTabState('editor')
    setIsOpenState(true)
  }, [])

  const value: AISidebarContextValue = {
    isOpen,
    open,
    close,
    toggle,
    setIsOpen,
    openWithQuery,
    pendingQuery,
    clearPendingQuery,
    activeTab,
    setActiveTab,
    editorElement,
    setEditorElement,
    isEditorMode,
    setIsEditorMode,
    editorBridge,
    setEditorBridge,
    openEditorTab,
    onOpenInNewTab,
    setOnOpenInNewTab,
    surface,
    setSurface,
    forceSpecialist,
    setForceSpecialist,
    regenerate,
    registerRegenerate,
  }
  
  return (
    <AISidebarContext.Provider value={value}>
      {children}
    </AISidebarContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

export function useAISidebar() {
  const context = useContext(AISidebarContext)
  if (!context) {
    throw new Error('useAISidebar must be used within AISidebarProvider')
  }
  return context
}

// Safe version that doesn't throw
export function useAISidebarSafe() {
  return useContext(AISidebarContext)
}


